'use client'
import { Input } from '@/shared/components/ui/input'
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select'
import { Select } from '@radix-ui/react-select'
import { Plus, Film, ChevronRight, MoreVertical, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/shared/lib/utils'

import { Project, ProjectEntity } from '@/shared/lib/db'
import { useAuth } from '@/features/auth'
import { useCreateProjectMutation, useProjectsQuery, useDeleteProjectMutation } from '@/core/project/queries/project.query'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import Page from '@/components/custom/Page'
import DialogBox from '@/features/dashboard/components/Dialog'
import { genresMap } from '@/features/dashboard/components/utils'

// --- New Premium Project Card
const ProjectCard = ({ project }: { project: ProjectEntity }) => {
    const imgInfo = genresMap[project.genre]
    const img = imgInfo?.image?.toString()
    const { mutate: deleteProject, isPending: isDeleting } = useDeleteProjectMutation()

    const handleDelete = (e: React.MouseEvent | Event) => {
        e.stopPropagation()
        deleteProject(project.id)
    }

    return (
        <a href={`/workspace/${project.id}`} className="block group w-full outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-[24px]">
            <div className="relative w-full aspect-[3/4] rounded-[24px] overflow-hidden border border-white/10 bg-[#0f0f11] shadow-xl transition-all duration-500 hover:shadow-2xl hover:border-primary/40 hover:-translate-y-2 cursor-pointer">

                {/* Genre Badge (Top Left Corner) */}
                <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/40 backdrop-blur-md px-3 py-1.5 border border-white/10 shadow-sm transition-transform duration-500 group-hover:scale-105">
                    <Film className="h-3 w-3 text-white/90" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-white/90">
                        {imgInfo?.label || project.genre}
                    </span>
                </div>

                {/* Options Menu (Top Right Corner) */}
                <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="p-2 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white/70 hover:text-white hover:bg-black/80 transition-all focus:outline-none"
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }}
                            >
                                <MoreVertical className="h-4 w-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 border-white/10 bg-[#0f0f11]/95 backdrop-blur-xl">
                            <DropdownMenuItem
                                onClick={handleDelete}
                                className="text-red-400 focus:text-red-300 focus:bg-red-400/10 cursor-pointer flex gap-2 items-center"
                                disabled={isDeleting}
                            >
                                <Trash2 className="h-4 w-4" />
                                <span>Delete Project</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Image Background */}
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-[1.03] opacity-80 mix-blend-screen"
                    style={{ backgroundImage: `url(${img})` }}
                />

                {/* Advanced Gradients for Mood & Vignette */}
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500" />
                <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />

                {/* Inner Glow Border */}
                <div className="absolute inset-0 rounded-[24px] ring-1 ring-inset ring-white/10 transition-all duration-500 group-hover:ring-white/20 pointer-events-none" />

                {/* Content Container */}
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 flex flex-col justify-end">

                    {/* Hover Lift Group */}
                    <div className="transform transition-transform duration-500 ease-out group-hover:-translate-y-2">

                        {/* Title */}
                        <h3 className="text-3xl font-extrabold tracking-tight text-white drop-shadow-md mb-2 line-clamp-3 leading-snug">
                            {project.title}
                        </h3>

                        {/* Hover Action */}
                        <div className="flex items-center gap-2 text-primary font-medium text-sm mt-4 opacity-0 transform translate-y-4 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-y-0">
                            <span>Open Project</span>
                            <ChevronRight className="h-4 w-4" />
                        </div>

                    </div>
                </div>
            </div>
        </a>
    )
}

const ProjectList = () => {
    const { user } = useAuth()
    const genreLabels = Object.entries(genresMap).map(([key, genre]) => ({
        key,
        label: genre.label,
    }))
    const [openDialog, setOpenDialog] = useState(false)

    const [newProject, setNewProject] = useState<Project | null>(null)
    const { data: projects } = useProjectsQuery(user?.uid)
    const { mutate: createProject } = useCreateProjectMutation()

    const addProject = async () => {
        if (!newProject) return

        const timestamp = new Date()
        const projectId = crypto.randomUUID()

        const localProject: ProjectEntity = {
            id: projectId,
            created_at: timestamp,
            updated_at: timestamp,
            title: newProject.title,
            genre: newProject.genre,
            subGenre: newProject.subGenre || null,
            userId: user!.uid,
        }

        createProject(localProject, {
            onSuccess: () => {
                setNewProject(null)
                setOpenDialog(false)
            },
        })
    }

    return (
        <Page title="Your Storyverse" subtitle="Explore and expand the worlds you've begun.">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 py-6">

                {/* Create New Project Card */}
                <DialogBox
                    title="Create Your Narrative World"
                    description="Build your storyverse from script to storyboard. Everything starts here."
                    onSubmit={addProject}
                    open={openDialog}
                    setOpen={setOpenDialog}
                    trigger={
                        <div className="group relative flex h-full min-h-[350px] w-full aspect-[3/4] cursor-pointer flex-col items-center justify-center gap-6 rounded-[24px] border-2 border-dashed border-white/10 bg-[#0f0f11]/50 backdrop-blur-sm transition-all duration-500 hover:border-primary/40 hover:bg-primary/5 hover:shadow-2xl hover:-translate-y-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">

                            {/* Inner ambient glow on hover */}
                            <div className="absolute inset-0 rounded-[24px] bg-primary/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                            <div className="relative rounded-2xl bg-white/5 border border-white/10 p-5 shadow-sm backdrop-blur-md transition-all duration-500 group-hover:scale-110 group-hover:bg-primary/20 group-hover:border-primary/30 group-hover:rotate-3 group-hover:shadow-primary/20">
                                <Plus className="h-10 w-10 text-white/50 group-hover:text-primary transition-colors duration-300" />
                            </div>

                            <div className="relative text-center px-6">
                                <h3 className="text-xl font-bold text-white/80 group-hover:text-white transition-colors">Start New Storyverse</h3>
                                <p className="mt-2 text-sm text-white/40 group-hover:text-white/60 transition-colors">
                                    Create a new project from scratch
                                </p>
                            </div>
                        </div>
                    }
                    body={
                        <div>
                            <Input
                                placeholder="Project Name"
                                className="mb-4"
                                value={newProject?.title ?? ''}
                                onChange={(e) =>
                                    setNewProject((prev) => ({
                                        ...prev!,
                                        title: e.target.value,
                                    }))
                                }
                            />
                            <Select
                                onValueChange={(e) => {
                                    setNewProject((prev) => ({
                                        ...prev!,
                                        genre: e,
                                    }))
                                }}
                            >
                                <SelectTrigger className="w-full mb-4">
                                    <SelectValue placeholder="Genre" />
                                </SelectTrigger>
                                <SelectContent>
                                    {genreLabels.map(({ key, label }, index) => (
                                        <SelectItem key={key} value={key}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    }
                />

                {/* Dynamically Loaded Project Cards */}
                {(projects || []).map((project, i) => (
                    <ProjectCard key={i} project={project} />
                ))}

            </div>
        </Page>
    )
}

export default ProjectList
